import { useState, useCallback } from 'react';

/**
 * Heart Animation Hook
 *
 * Manages the state and lifecycle of the heart animation that appears on double-tap like.
 * Uses CSS animations with animationEnd event for cleanup (no setTimeout needed).
 *
 * @returns {Object} Animation controls
 * @returns {boolean} returns.showHeart - Whether to show the heart
 * @returns {Function} returns.triggerHeartAnimation - Function to trigger the animation
 * @returns {Function} returns.handleAnimationEnd - Callback for CSS animationend event
 *
 * @example
 * const { showHeart, triggerHeartAnimation, handleAnimationEnd } = useHeartAnimation();
 *
 * // Trigger animation on like
 * const handleLike = () => {
 *   triggerHeartAnimation();
 *   // ... rest of like logic
 * };
 *
 * // Render heart conditionally
 * {showHeart && <Heart onAnimationEnd={handleAnimationEnd} />}
 */
export const useHeartAnimation = () => {
  const [showHeart, setShowHeart] = useState(false);

  /**
   * Triggers the heart animation by setting showHeart to true.
   * The animation will auto-hide when CSS animation completes via handleAnimationEnd.
   */
  const triggerHeartAnimation = useCallback(() => {
    setShowHeart(true);
  }, []);

  /**
   * Handles the CSS animationend event to hide the heart after animation completes.
   * This ensures cleanup happens naturally without setTimeout.
   */
  const handleAnimationEnd = useCallback(() => {
    setShowHeart(false);
  }, []);

  return {
    showHeart,
    triggerHeartAnimation,
    handleAnimationEnd,
  };
};
